/**
 * 案件へ新しい要求を立てる（作り直しの要求の層 5 表）— 採番して入れるだけの経路。
 *
 * 作り直しの書込ツールは `requirement` に**既にある要求の番号しか受けない**（`_element-write.ts`
 * の `missing_requirement`）。ところが新しい表に要求を立てる経路は復元の入れ直し
 * （`requirement-restore.ts`）しか無く、復元の対象でない案件は 1 本目の要求を立てられなかった
 * （案件 7 のストーリーの統合がここで止まった）。このモジュールがその 1 本目を立てる。
 *
 * **入力の形は要求の契約（`contract/requirement-input.ts`）の「その場で作る」形に揃える。** 要求は
 * why を 1 件以上、why は根拠を 1 件以上持つ（制約 B）。違いは既存の why を番号で指す形を受けない
 * ことだけである — 今の契約の番号は旧表の UUID で、作り直しの表の番号と混ぜられない。
 *
 * **冪等である。** その案件に同じ `statement` の要求が既に在れば、立てずにその番号を返す。
 * ローカルの適用スクリプトを撃ち直しても、要求が 2 本にならない。
 *
 * **dry-run では採番しない。** 検査だけのために番号を配ると、撃つたびに欠番が増える
 * （`versioned-write.ts` と同じ判断）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件へ新しい要求を why・根拠と一緒に立てる。同じ文の要求が在れば番号を返し、無ければ採番して要求の層 5 表へ入れる
 * @graph-connects none
 */
import { z } from 'zod';
import { SPEC_ELEMENT_TABLE_IDS, } from '../../contract/elements/index.js';
import { nonEmptyString } from '../../contract/node-schemas.js';
import { evidenceInputSchema, requirementStatementString, } from '../../contract/requirement-input.js';
import { isPjMerged } from './pj-changes.js';
import { buildInsertRequirementLayerParams, buildInsertRequirementLayerScript, } from './requirement-restore.js';
import { tableRef } from './store.js';
import { toInputSchemaRejections } from './write.js';
/**
 * その場で作る why 1 件の入力。根拠を 1 件以上持つ。
 * @graph-connects none
 */
const elementNewWhyInputSchema = z.strictObject({
    statement: nonEmptyString(),
    outcome: nonEmptyString(),
    evidences: z.array(evidenceInputSchema).min(1),
});
/**
 * 新しい要求 1 件の入力。**要求の契約の「その場で作る」形と同じ 3 欄**である。
 *
 * 要求の文は上限の字数以内でなければ立てられない（`requirementStatementString`）。同じ文の
 * 要求の引き当てより前に止まるので、上限より長い既存の文と同じ文を渡しても番号は返らない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 新しい要求 1 件の入力スキーマ（実現すること〔上限の字数以内〕・受入条件・why 1 件以上。why は根拠 1 件以上）
 * @graph-connects requirementStatementString [calls] 要求の文の上限の字数の検査
 */
export const elementNewRequirementInputSchema = z.strictObject({
    statement: requirementStatementString(),
    acceptance: nonEmptyString(),
    whys: z.array(elementNewWhyInputSchema).min(1),
});
/**
 * 新しい要求の 5 表の行を組む。**番号は渡されたものを使う**（採番は呼び出し側）。
 *
 * why は入力の順に `whyFrom` から、根拠は why の順・why の中の順に `evidenceFrom` から番号を当てる。
 *
 * @param args 案件番号・入力・要求の番号・why と根拠の最初の番号・立てた時刻・立てた主体
 * @returns 5 表へ入れる行
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 新しい要求 1 件の入力と採番した番号から、要求の層 5 表へ入れる行を組む
 * @graph-connects none
 */
export function planElementRequirementRows(args) {
    const { input, requirementId, at, actor } = args;
    const whys = [];
    const whyEvidences = [];
    const requirementWhys = [];
    let evidenceId = args.evidenceFrom;
    input.whys.forEach((why, index) => {
        const whyId = args.whyFrom + index;
        whys.push({ whyId, statement: why.statement, outcome: why.outcome, raisedAt: at, actor });
        requirementWhys.push({ requirementId, whyId });
        for (const evidence of why.evidences) {
            whyEvidences.push({ whyId, evidenceId, ...evidence });
            evidenceId += 1;
        }
    });
    const requirements = [
        {
            requirementId,
            statement: input.statement,
            acceptance: input.acceptance,
            raisedAt: at,
            actor,
        },
    ];
    const pjRequirements = [
        { pjNumber: args.pjNumber, requirementId },
    ];
    return { requirements, whys, whyEvidences, pjRequirements, requirementWhys };
}
/**
 * 案件へ新しい要求を 1 件立てる。
 *
 * 手順は 4 つである。①入力を契約に通す ②確定済みの案件なら止める ③同じ文の要求がその案件に
 * 在ればその番号を返す ④採番して 5 表へ入れる（dry-run なら ③ までで止める）。
 *
 * @param args 案件番号・入力（形式検査の前の生の値）・検査だけか・立てた主体
 * @param deps 置き場・案件の変更の読取・採番・時刻
 * @returns 立てた / 既に在った / 検査だけ / 拒否
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件へ新しい要求を 1 件立てる。形式検査・確定済みの拒否・同じ文の要求の引き当て・採番・5 表への追加
 * @graph-connects planElementRequirementRows [calls] 5 表の行の組み立て
 * @graph-connects toInputSchemaRejections [calls] 形式検査の結果を拒否の一覧へ直す
 * @graph-connects isPjMerged [calls] 案件が確定済みかの判定
 */
export async function createElementRequirement(args, deps) {
    const parsed = elementNewRequirementInputSchema.safeParse(args.input);
    if (!parsed.success) {
        return {
            outcome: 'rejected',
            rejections: toInputSchemaRejections('requirement', parsed.error),
        };
    }
    const input = parsed.data;
    if (isPjMerged(await deps.changes.listPjChanges(args.pjNumber))) {
        return {
            outcome: 'rejected',
            rejections: [
                {
                    reason: 'pj-already-merged',
                    pointer: 'pj_number',
                    message: `案件 ${String(args.pjNumber)} は確定済みなので要求を立てられない`,
                },
            ],
        };
    }
    const existing = await deps.requirements.findPjRequirementIdByStatement(args.pjNumber, input.statement);
    if (existing !== null)
        return { outcome: 'existing', requirementId: existing };
    const evidenceCount = input.whys.reduce((total, why) => total + why.evidences.length, 0);
    if (args.dryRun) {
        return { outcome: 'preview', input, whyCount: input.whys.length, evidenceCount };
    }
    const requirementId = (await deps.allocateIds('requirements', 1)).from;
    const whyFrom = (await deps.allocateIds('whys', input.whys.length)).from;
    const evidenceFrom = (await deps.allocateIds('whyEvidences', evidenceCount)).from;
    const rows = planElementRequirementRows({
        pjNumber: args.pjNumber,
        input,
        requirementId,
        whyFrom,
        evidenceFrom,
        at: deps.now(),
        actor: args.actor,
    });
    await deps.requirements.insertRequirementLayer(rows);
    return { outcome: 'created', requirementId, rows };
}
/**
 * 同じ文の要求を案件の結びから引く文。
 *
 * 案件と要求の結びはクラスタ先頭列（`pjNumber`）の等値で刈れる。要求の本文は結びが指す番号で
 * 引く。並べてから 1 件に絞るのは、同じ文の要求が既に 2 本在る案件でも答えが揺れないためである。
 *
 * @param config 接続先
 * @returns 投げる `SELECT` 文
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件に結ばれた要求のうち、文が一致するものの番号を 1 件引く SELECT を組む
 * @graph-connects bigquery.spec_graph_pj_requirements [reads_from, via:pjNumber] 案件に結ばれた要求の番号
 * @graph-connects bigquery.spec_graph_requirements [reads_from, via:requirementId] 要求の本文
 */
export function buildFindPjRequirementByStatementSql(config) {
    return `SELECT r.requirementId AS requirementId
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.pjRequirements)} AS p
JOIN ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.requirements)} AS r USING (requirementId)
WHERE p.pjNumber = @pjNumber AND r.statement = @statement
ORDER BY r.requirementId
LIMIT 1`;
}
/**
 * BigQuery が返した値を整数に戻す。大きい値は `{ value }` のラッパで返る。
 * @graph-connects none
 */
function toInteger(value) {
    const raw = value !== null && typeof value === 'object' && 'value' in value
        ? value.value
        : value;
    const parsed = typeof raw === 'number' ? raw : Number(String(raw));
    if (!Number.isInteger(parsed)) {
        throw new Error(`spec-graph: 要求の番号が整数で返らなかった: ${String(value)}`);
    }
    return parsed;
}
/**
 * 新しい要求を立てる経路の置き場の BigQuery 実装。
 *
 * @param config 接続先
 * @returns 2 操作の実装
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 新しい要求を立てる経路の置き場（同じ文の要求の引き当て・5 表への追加）の BigQuery 実装
 * @graph-connects buildFindPjRequirementByStatementSql [calls] 同じ文の要求を引く文
 * @graph-connects buildInsertRequirementLayerScript [calls] 5 表へ入れるだけのスクリプト
 * @graph-connects buildInsertRequirementLayerParams [calls] 5 表へ入れるスクリプトのパラメータ
 * @graph-connects toInteger [calls] 返った要求の番号の読み直し
 * @graph-connects bigquery.spec_graph_pj_requirements [reads_from, via:pjNumber] 案件に結ばれた要求の番号
 * @graph-connects bigquery.spec_graph_requirements [reads_from, via:requirementId] 同じ文の要求の引き当て
 * @graph-connects bigquery.spec_graph_requirements [writes_to, via:requirementId] 要求の本文の追加
 * @graph-connects bigquery.spec_graph_whys [writes_to, via:whyId] why の本文の追加
 * @graph-connects bigquery.spec_graph_why_evidences [writes_to, via:whyId] 根拠の追加
 * @graph-connects bigquery.spec_graph_pj_requirements [writes_to, via:requirementId] 案件と要求の結びの追加
 * @graph-connects bigquery.spec_graph_requirement_whys [writes_to, via:requirementId] 要求と why の結びの追加
 */
export function createBigQueryElementRequirementWriteStore(config) {
    const location = config.location === undefined ? {} : { location: config.location };
    return {
        async findPjRequirementIdByStatement(pjNumber, statement) {
            const [rows] = await config.client.query({
                query: buildFindPjRequirementByStatementSql(config),
                params: { pjNumber, statement },
                ...location,
            });
            const first = rows[0];
            return first === undefined ? null : toInteger(first['requirementId']);
        },
        async insertRequirementLayer(rows) {
            await config.client.query({
                query: buildInsertRequirementLayerScript(config, rows),
                params: buildInsertRequirementLayerParams(rows),
                ...location,
            });
        },
    };
}
/**
 * 新しい要求を立てる経路の置き場のメモリ実装。
 *
 * @param rows 初期の 5 表。省略すると空から始める
 * @returns 2 操作の実装と、保持している 5 表
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 新しい要求を立てる経路の置き場のメモリ実装（テストと BigQuery 無しの実行用）
 * @graph-connects none
 */
export function createInMemoryElementRequirementWriteStore(rows) {
    const state = {
        rows: rows ?? {
            requirements: [],
            whys: [],
            whyEvidences: [],
            pjRequirements: [],
            requirementWhys: [],
        },
    };
    return {
        store: {
            findPjRequirementIdByStatement(pjNumber, statement) {
                const linked = new Set(state.rows.pjRequirements
                    .filter((row) => row.pjNumber === pjNumber)
                    .map((row) => row.requirementId));
                const found = state.rows.requirements
                    .filter((row) => linked.has(row.requirementId) && row.statement === statement)
                    .map((row) => row.requirementId)
                    .sort((left, right) => left - right)[0];
                return Promise.resolve(found ?? null);
            },
            insertRequirementLayer(added) {
                state.rows = {
                    requirements: [...state.rows.requirements, ...added.requirements],
                    whys: [...state.rows.whys, ...added.whys],
                    whyEvidences: [...state.rows.whyEvidences, ...added.whyEvidences],
                    pjRequirements: [...state.rows.pjRequirements, ...added.pjRequirements],
                    requirementWhys: [...state.rows.requirementWhys, ...added.requirementWhys],
                };
                return Promise.resolve();
            },
        },
        state,
    };
}
//# sourceMappingURL=requirement-create.js.map